import { useCallback, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@apollo/client';
import { Heading } from 'react-aria-components';
import { NicknameForm } from 'src/components/auth';
import BackButton from 'src/components/BackButton';
import PageLoader from 'src/components/ui/PageLoader';
import PasskeyIcon from 'src/assets/icons/passkey.svg';
import CLIENT_PASSKEYS from 'src/graphql/query/user-passkeys.graphql';

import type { ClientWebAuthn } from 'src/types/client';

const EditPasskeyPage = () => {
  const { passkey } = Route.useParams();
  const navigate = useNavigate();
  const { currentPasskey, loading } = useCurrentPasskey(passkey);

  const onCompleted = useCallback(
    () => navigate({ to: '/profile', hash: 'security', replace: true }),
    [navigate]
  );

  if (loading) return <PageLoader />;

  return (
    <>
      <div className="flex items-center gap-2 px-6 py-4">
        <BackButton to="/profile" hash="security" />
      </div>
      <div className="bg-surface-alt rounded-3xl shadow-3 px-4 sm:px-6 pt-10 pb-6 flex flex-col justify-center items-center">
        <PasskeyIcon className="w-14 h-14" />
        <Heading
          level={5}
          className="text-center text-surface-alt-90 text-3xl font-bold mt-6 mb-16"
        >
          Change passkey nickname
        </Heading>

        <NicknameForm
          id={Number(passkey)}
          defaultValues={{ nickname: currentPasskey?.name }}
          onCompleted={onCompleted}
        />
      </div>
    </>
  );
};

const useCurrentPasskey = (passkey: string) => {
  const { loading, data } = useQuery(CLIENT_PASSKEYS);
  const currentPasskey = useMemo(() => {
    const list = data?.user.passkeys ?? [];

    return list.find(
      (p: ClientWebAuthn) => p.clientWebauthnId === Number(passkey)
    );
  }, [data?.user.passkeys, passkey]);

  return {
    loading,
    currentPasskey,
  };
};

export const Route = createFileRoute(
  '/_authenticated/_layout-basic/passkey/$passkey'
)({
  component: EditPasskeyPage,
});
